import { commonClient } from 'api';
import { AnnouncementBar, AnnouncementModal } from 'components';
import AppShell from 'components/AppShell/AppShell';
import { NUMBER_OF_DAYS } from 'components/Header/UserNotifications';
import dayjs from 'dayjs';
import { getVisibleAnnouncements } from 'helpers/announcementsHelper';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { getNow } from 'helpers/timeHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { Announcement, AnnouncementToRender } from './models';
import {
  useDismissAnnouncementMutation,
  useGetCompanyAndUserInfoQuery,
  useGetCurrentAnnouncementsQuery
} from './queries';
import useAuth from './useAuth';

interface Props {}

interface AnnouncementsContextType {
  systemAnnouncements: AnnouncementToRender[];
}

const AnnouncementsContext = createContext<AnnouncementsContextType>({
  systemAnnouncements: []
});

const AnnouncementsProvider: React.FC<PropsWithChildren<Props>> = (props): React.ReactElement => {
  const { authenticated } = useAuth();
  const { t } = useCustomTranslation();

  const companyAndUserInfoQuery = useGetCompanyAndUserInfoQuery(authenticated);
  const announcmentsQuery = useGetCurrentAnnouncementsQuery();
  const dismissAnnouncementMutation = useDismissAnnouncementMutation();
  const [systemAnnouncements, setSystemAnnouncements] = useState<AnnouncementToRender[]>([]);

  const appUser = companyAndUserInfoQuery.data?.user;
  const licence = companyAndUserInfoQuery.data?.licence;
  const isSubscriptionsEnabled = companyAndUserInfoQuery.data?.company.isSubscriptionsEnabled;
  const isSuperAdmin = appUser?.isSuperAdmin || false;

  const memoedValue = useMemo<AnnouncementsContextType>(
    () => ({
      systemAnnouncements
    }),
    [systemAnnouncements]
  );

  const handleRequestEmailConfirmation = async () => {
    try {
      await commonClient.requestEmailConfirmation();
    } catch (e) {
      handleApiErrorWithNotification(e);
    }
  };

  const lessThanWeekRemaining = (expires: string): boolean => {
    const remainingDays = dayjs.duration(dayjs.utc(expires).diff(getNow())).asDays();

    if (remainingDays < NUMBER_OF_DAYS) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const announcements: AnnouncementToRender[] = [];

    if (appUser && !appUser.isSuperAdmin) {
      if (appUser.email && !appUser.hasEmailConfirmed) {
        announcements.push({
          id: 'unconfrimedEmailNotify',
          title: t('notifications.unconfirmedEmail.title'),
          description: (
            <>
              {`${t('notifications.unconfirmedEmail.text')} `}
              <a onClick={() => handleRequestEmailConfirmation()}>
                {`${t('notifications.unconfirmedEmail.recoveryText')} `}
              </a>
            </>
          ),
          type: 'Info',
          isDismissed: false
        });
      }

      if (!isSubscriptionsEnabled && !licence && (appUser.isAdmin || appUser.isManager)) {
        announcements.push({
          id: 'missingLicenceNotify',
          title: t('notifications.missingLicence.title'),
          description: t('notifications.missingLicence.text'),
          type: 'Warning',
          isDismissed: false
        });
      }

      if (
        !isSubscriptionsEnabled &&
        licence?.expiresAt &&
        lessThanWeekRemaining(licence.expiresAt) &&
        (appUser.isAdmin || appUser.isManager)
      ) {
        announcements.push({
          id: 'expireLicenceNotify',
          title: t('notifications.expireLicence.title'),
          description: t('notifications.expireLicence.text', {
            expires: dayjs.utc(licence.expiresAt).format('LL')
          }),
          type: 'Info',
          isDismissed: false
        });
      }
    }

    setSystemAnnouncements(announcements);
  }, [appUser, licence, t, isSubscriptionsEnabled]);

  if (isSuperAdmin) {
    return <AppShell>{props.children}</AppShell>;
  }

  const handleDismissAnnouncement = (id: string) => {
    if (systemAnnouncements.find((x) => x.id === id)) {
      setSystemAnnouncements((prevState) =>
        prevState.map((announcement) => {
          if (id === announcement.id) {
            return { ...announcement, isDismissed: true };
          }
          return announcement;
        })
      );
    } else {
      dismissAnnouncementMutation.mutate(id);
    }
  };

  const announcementsToPublish: AnnouncementToRender[] = [
    ...getVisibleAnnouncements(announcmentsQuery.data?.filter((x) => !x.isDismissed) || []),
    ...systemAnnouncements.filter((x) => !x.isDismissed)
  ];

  return (
    <AnnouncementsContext.Provider value={memoedValue}>
      <AppShell>
        {announcementsToPublish?.map((announcement) => {
          if (announcement.type === 'InfoModal') {
            return (
              <AnnouncementModal
                key={announcement.id}
                announcement={announcement as Announcement}
                onDismiss={() => dismissAnnouncementMutation.mutate(announcement.id)}
              />
            );
          }

          return (
            <AnnouncementBar
              key={announcement.id}
              announcement={announcement}
              onDismiss={() => handleDismissAnnouncement(announcement.id)}
            />
          );
        })}
        {props.children}
      </AppShell>
    </AnnouncementsContext.Provider>
  );
};

export default AnnouncementsProvider;

export function useSystemAnnouncements() {
  return useContext(AnnouncementsContext);
}
